// ============================================================================
// customContentDeleteOps.js — CW-DELETE-AFFORDANCE (2026-07-11)
// ============================================================================
// [PURPOSE]: Pure playerStats-transform functions for deleting custom content.
//   Extracted so App.jsx delete handlers, FeatsPanel.deleteCustomFeat, and the
//   guard tests all exercise the SAME production logic (Rule 22 —
//   extract → import → assert; template: src/utils/skillProficiencies.js).
//
// [SCOPE]: Only the playerStats-backed content stores. Race/Class/Subclass
//   live in sourcesManager (delete via removeContentFromSource) and custom
//   spells live in knownSpells — both tracked on board row
//   CW-TRIO-STORAGE-DISCONNECT, not handled here.
//
// [RULE 31]: No shape change — every function returns a new playerStats with
//   entries filtered out; untouched slots are passed through by reference.

/**
 * Remove one entry from an array-shaped custom-content slot
 * (customConditions / customBoons / customBackgrounds).
 *
 * Effect cleanup is automatic where it matters: useBoonEffects re-registers
 * from playerStats.customBoons on every change (removeByPrefix('boon:') then
 * re-add), so a deleted boon's passive effects drop out on the same commit.
 *
 * @param {Object} prev — previous playerStats
 * @param {string} slot — slot name, e.g. 'customBoons'
 * @param {string} id — entry id to remove
 * @returns {Object} next playerStats
 */
export function removeCustomEntry(prev, slot, id) {
  const list = Array.isArray(prev?.[slot]) ? prev[slot] : [];
  return { ...prev, [slot]: list.filter((e) => e && e.id !== id) };
}

/**
 * Remove a custom feat from BOTH stores it can live in: the authored catalog
 * (customFeats) and the character's selected-feats list (feats). Mirrors the
 * original FeatsPanel.deleteCustomFeat semantics — that handler now delegates
 * here so the two call sites can't drift.
 *
 * @param {Object} prev — previous playerStats
 * @param {string} featId — feat id to remove
 * @returns {Object} next playerStats
 */
export function removeCustomFeatEverywhere(prev, featId) {
  return {
    ...prev,
    customFeats: prev?.customFeats?.filter((f) => f.id !== featId) || [],
    feats: prev?.feats?.filter((f) => f.id !== featId) || [],
  };
}

/**
 * Remove a custom monster from the id-keyed monsterCatalog object.
 *
 * @param {Object} prev — previous playerStats
 * @param {string} monsterId — catalog key to remove
 * @returns {Object} next playerStats
 */
export function removeCustomMonster(prev, monsterId) {
  const cat = { ...(prev?.monsterCatalog || {}) };
  delete cat[monsterId];
  return { ...prev, monsterCatalog: cat };
}
